/**
 * The loyalty club at the till (club A, spec §10) — the pure rules the POS page, the cart panel and the store
 * share.
 *
 * The till shows; the server decides (190). A club price is sent only for a customer the server confirmed a
 * member (a card scan, a link), online, with the shop's switch on. When create_sale still refuses it — the owner
 * switched the club off on his phone, a return dropped the month below the threshold — the till re-reads what
 * changed, re-prices and shows the new total before another press (Codex #I4).
 */
import type { Key } from './i18n'
import { phoneNorm, phoneSearchable, type ClubStatus } from './club'

export type ClubRefresh = 'customers' | 'settings' | 'products'

export function afterClubRefusal(message: string): { refresh: ClubRefresh[]; message: Key } | null {
  if (/\bCLUB_OFF\b/.test(message)) return { refresh: ['customers', 'settings', 'products'], message: 'club.till.refusedOff' }
  if (/\bCLUB_NOT_MEMBER\b/.test(message)) return { refresh: ['customers', 'products'], message: 'club.till.refusedMember' }
  return null
}

/** Club prices on the cart: online only — the till never queues a club-priced sale (the 1.58 group rule). */
export function clubModeOn(s: { online: boolean; clubFeature: boolean; customerId: string; status: ClubStatus | null | undefined }): boolean {
  return s.online && s.clubFeature && !!s.customerId && s.status?.member === true
}

type Findable = {
  name: string; phone: string | null; joined_via?: string | null; phone_norm?: string | null
  phone_confirmed_at?: string | null; anonymized_at?: string | null
}
const digits = (s: string | null | undefined) => (s ?? '').replace(/\D/g, '')

/**
 * The customer picker's one rule: the name, or a run of the phone's digits (2+, so „599 12" finds „599-12-…") —
 * but a SELF-typed phone is no search key until a cashier confirmed it with the person in front of them
 * (spec §6, Codex #I6); a staff-made customer is found by phone exactly as before 1.63. A confirmed phone also
 * matches typed with the country code. An anonymized customer („კლუბიდან წაშლა") is never offered.
 */
export function customerMatches(c: Findable, rawTerm: string): boolean {
  if (c.anonymized_at) return false
  const term = rawTerm.trim().toLowerCase()
  if (term === '') return true
  if (c.name.toLowerCase().includes(term)) return true
  if (!phoneSearchable(c)) return false
  const td = digits(term)
  if (td.length >= 2 && digits(c.phone).includes(td)) return true
  const n = phoneNorm(term)
  return !!n && !!c.phone_confirmed_at && c.phone_norm === n
}

/** A customer's cached standing after a sale or a return with him: stale, so the next pick asks the server again. */
export function withoutStatus<T>(statuses: Record<string, T>, customerId: string | null | undefined): Record<string, T> {
  if (!customerId || !(customerId in statuses)) return statuses
  const { [customerId]: _stale, ...rest } = statuses
  return rest
}

/** Two card scans in a row (Codex final #9): each call takes a number; only the latest one's answer may land. */
export function latestCall(): { next: () => number; isLatest: (n: number) => boolean } {
  let last = 0
  return { next: () => ++last, isLatest: (n) => n === last }
}
